import { useIsMobileHook } from '@teliaee/telia-common-components';
import { TranslationsContext } from '@teliaee/telia-translate';
import * as React from 'react';
import { Card, CardContent, Heading, Separator, Table, TableCell, TableRow } from 'telia-front-react';
import { removeMarkUpHtmlFromString, renderValueAsHtml } from '../util/PriceListUtil';
import { PriceListCategory, PriceListSubCategory, PriceRow } from './PriceListInterfaces';

export interface PriceListTabContentProps {
  category: PriceListCategory;
  categoryIndex: number;
  isPrivateCustomer: boolean;
  showPricesWithVat: boolean;
  isFilteredResults?: boolean;
}

const setMobileTableRowBackgroundColor = (index: number): string | undefined => {
  return index % 2 !== 0 ? 'neutral-bg-level-2' : undefined;
};

const PriceListTabContent = (props: PriceListTabContentProps) => {
  const { category, categoryIndex, isPrivateCustomer, showPricesWithVat } = props;
  const displayPriceWithVat = isPrivateCustomer || showPricesWithVat;

  const translate = React.useContext(TranslationsContext).translate;

  const { isMobile } = useIsMobileHook();

  const renderSubCategory = (subCategory: PriceListSubCategory, mainCategoryIndex: number, subCategoryIndex: number) => {
    if (props.isFilteredResults && !subCategory.containsSearchTerm) {
      return null;
    }

    return (
      <React.Fragment key={`${mainCategoryIndex}-subcategory-${subCategoryIndex}`}>
        <Heading element="h4" className="text-bold">
          {mainCategoryIndex}.{subCategoryIndex} {renderValueAsHtml(subCategory.name)}
        </Heading>
        <Separator color="invisible" spacing="xsmall" />
        {isMobile ? renderPricesTableForMobile(subCategory.name, subCategory.priceRows) : renderPricesTableForDesktop(subCategory.name, subCategory.priceRows)}
        <Separator color="invisible" spacing="small" />
        {renderPriceReferences(subCategory.name, subCategory.references)}
        <Separator color="invisible" spacing="small" />
        {renderBulletPoints(subCategory.bulletPoints, mainCategoryIndex, subCategoryIndex)}
      </React.Fragment>
    );
  };

  const renderPricesTableForDesktop = (tableName: string, priceRows: PriceRow[][]) => (
    <Table key={tableName}>
      <thead>
        <TableRow>
          <TableCell type="th" scope="col" width={8} padding="xsmall">
            {removeMarkUpHtmlFromString(tableName)}
          </TableCell>
          {!isPrivateCustomer && (
            <TableCell type="th" scope="col" className="text-right" width={2} padding="xsmall">
              <p>{translate('pricelist.priceWithoutVat')}</p>
            </TableCell>
          )}
          {displayPriceWithVat && (
            <TableCell type="th" scope="col" className="text-right" width={2} padding="xsmall">
              <p>{translate('pricelist.priceWithVat')}</p>
            </TableCell>
          )}
        </TableRow>
      </thead>
      <tbody>{getPricesTableBody(tableName, priceRows)}</tbody>
    </Table>
  );

  const renderPricesTableForMobile = (tableName: string, priceRows: PriceRow[][]) => (
    <Table key={tableName}>
      <tbody>{getPricesTableBody(tableName, priceRows)}</tbody>
    </Table>
  );

  const getPricesTableBody = (tableName: string, priceRows: PriceRow[][]) => {
    return priceRows.map((priceRowList, index) => {
      let separatorRow = undefined;
      const firstPrice = priceRowList[0];
      const shouldAddSeparatorRow = index !== 0 && !!firstPrice && !firstPrice.price && !firstPrice.priceWithVat && !firstPrice.unit;
      if (!isMobile && shouldAddSeparatorRow) {
        separatorRow = (
          <TableRow key={`separatorRow-${tableName}-${index}`}>
            <TableCell />
          </TableRow>
        );
      }
      const prices = priceRowList.map((priceRow, priceRowIndex) => {
        const references = getPriceRowReferenceIdsAsString(priceRow.referenceIds);
        const isPriceRowGroupNameRow = shouldAddSeparatorRow && !priceRow.price && !priceRow.priceWithVat && !priceRow.unit;
        const backgroundColor = !isPriceRowGroupNameRow && setMobileTableRowBackgroundColor(priceRowIndex !== 0 ? priceRowIndex : index);

        return isMobile
          ? renderMobilePriceRowsForPriceRow(tableName, priceRow, priceRowIndex, backgroundColor, references)
          : renderDesktopPriceRow(priceRow, priceRowIndex, references);
      });
      return separatorRow ? [separatorRow, ...prices] : prices;
    });
  };

  const renderMobilePriceRowsForPriceRow = (tableName: string, priceRow: PriceRow, priceRowIndex: number, backgroundColor: string, references: string) => (
    <React.Fragment key={`${tableName}-${priceRowIndex}-${priceRow.name}`}>
      {renderMobilePriceRow(removeMarkUpHtmlFromString(tableName), priceRow.name, backgroundColor, references)}
      {!isPrivateCustomer &&
        (priceRow.price || priceRow.unit) &&
        renderMobilePriceRow(translate('pricelist.priceWithoutVat'), `${priceRow.price} ${priceRow.unit}`, backgroundColor)}
      {displayPriceWithVat &&
        (priceRow.priceWithVat || priceRow.unit) &&
        renderMobilePriceRow(translate('pricelist.priceWithVat'), `${priceRow.priceWithVat} ${priceRow.unit}`, backgroundColor)}
    </React.Fragment>
  );

  const renderMobilePriceRow = (key: string, value: string, backgroundColor: string, references?: string) => (
    <TableRow className={backgroundColor}>
      <TableCell width={5} className="text-light" padding="xsmall">
        <p>{renderValueAsHtml(key)}</p>
      </TableCell>
      <TableCell width={7} padding="xsmall">
        <p>{renderValueAsHtml(value)}</p>
        {!!references && <sup> ({references})</sup>}
      </TableCell>
    </TableRow>
  );

  const renderDesktopPriceRow = (priceRow: PriceRow, priceRowIndex: number, references: string) => (
    <TableRow key={`${priceRow.name}-${priceRowIndex}`}>
      <TableCell width={8} padding="xsmall">
        <p>
          {renderValueAsHtml(priceRow.name)}
          {!!references && <sup> ({references})</sup>}
        </p>
      </TableCell>
      {!isPrivateCustomer && (
        <TableCell className="text-right" width={2} padding="xsmall">
          <p>
            {renderValueAsHtml(priceRow.price)} {renderValueAsHtml(priceRow.unit)}
          </p>
        </TableCell>
      )}
      {displayPriceWithVat && (
        <TableCell className="text-right" width={2} padding="xsmall">
          <p>
            {renderValueAsHtml(priceRow.priceWithVat)} {renderValueAsHtml(priceRow.unit)}
          </p>
        </TableCell>
      )}
    </TableRow>
  );

  const getPriceRowReferenceIdsAsString = (referenceIds: string[]): string => {
    const referencesAmount = referenceIds.length;
    if (referencesAmount === 0) {
      return '';
    }
    return referencesAmount === 1 ? referenceIds[0] : referenceIds.join(', ');
  };

  const renderPriceReferences = (subCategoryName: string, references: { [key: string]: string }) => {
    const referencesKeys = Object.keys(references);
    if (referencesKeys.length < 1) {
      return null;
    }
    return (
      <Card>
        <CardContent>
          {referencesKeys.map((key, index) => {
            return (
              <React.Fragment key={`${subCategoryName}-reference-${index}`}>
                <p>
                  <sup>({key})</sup> {renderValueAsHtml(references[key])}
                </p>
                {index < referencesKeys.length - 1 && <Separator color="invisible" spacing="xsmall" />}
              </React.Fragment>
            );
          })}
        </CardContent>
      </Card>
    );
  };

  const renderBulletPoints = (bulletPoints: string[], mainCategoryIndex: number, subCategoryIndex: number) => {
    if (!bulletPoints || bulletPoints.length < 1) {
      return null;
    }
    return (
      <ul key={`${mainCategoryIndex}.${subCategoryIndex}-bulletpoints`}>
        {bulletPoints.map((bulletPoint, bulletPointIndex) => {
          return <li key={`${mainCategoryIndex}.${subCategoryIndex}-bulletpoint-${bulletPointIndex}`}>{renderValueAsHtml(bulletPoint)}</li>;
        })}
      </ul>
    );
  };

  return (
    <>
      <Heading element="h3">
        {categoryIndex}. {renderValueAsHtml(category.categoryTitle)}
      </Heading>
      <Separator color="invisible" spacing="xsmall" />
      {category.subCategories.map((subCategory, subCategoryIndex) => renderSubCategory(subCategory, categoryIndex, subCategoryIndex + 1))}
    </>
  );
};

export default PriceListTabContent;
